<template>
  <div data-wd="rootView">
    <router-view name="admin" />

    <rg-loader v-if="pending" />
    <template v-else>
      <div v-if="isAfterClosing" class="ui container" :class="$style.notice">
        <div class="ui huge message">
          <p>
            <strong
              v-html="$t('closingMessage', { closingTime: closingTimeLocale })"
            />
          </p>
        </div>
      </div>
      <template v-else>
        <div v-if="appBlocked" class="ui container" :class="$style.notice">
          <div class="ui huge message">
            <p><strong v-html="access.blockedMessage" /></p>
          </div>
        </div>

        <template v-else>
          <router-view name="toolbar" />
          <rg-sidenav />
          <div :class="$style.toolbarPadder">
            <app-closing-banner v-if="isAfterNotice" />
            <div :class="{ [$style.padder]: !$route.meta.noRootPadding }">
              <div
                :class="{ [$style.contentWidth]: !$route.meta.noContentWidth }"
              >
                <div v-if="viewErrorCode">
                  <empty-state>
                    <p slot="content">
                      {{ errorMessage }}
                    </p>
                    <div slot="function">
                      <router-link
                        class="ui icon button large"
                        :to="{ name: 'library' }"
                        replace
                      >
                        {{ $t("terms.backToLibrary") | cap }}
                      </router-link>
                      <a
                        v-show="unknownError"
                        :class="$style.mail"
                        :href="emailHref"
                      >
                        {{ $t("links.email") }}
                      </a>
                    </div>
                  </empty-state>
                </div>

                <div v-else>
                  <rg-loader v-show="viewPending">
                    <span>{{ $t("inprogress") }}</span>
                  </rg-loader>
                  <keep-alive
                    v-if="isLoggedIn"
                    :include="namesForCache"
                    :max="5"
                  >
                    <router-view v-show="!viewPending" :key="$route.fullPath" />
                  </keep-alive>
                  <router-view
                    v-else
                    v-show="!viewPending"
                    :key="$route.fullPath"
                  />
                </div>
              </div>
            </div>
          </div>

          <button-to-top />
        </template>
      </template>
    </template>
  </div>
</template>

<script>
import RgToolbar from '@/components/Toolbar.vue'
import RgSidenav from '@/components/Sidenav.vue'
import RgLoader from '@/components/Loader.vue'
import ButtonToTop from '@/components/ButtonToTop.vue'
import EmptyState from '@/components/EmptyState.vue'
import { mapGetters } from 'vuex'
import * as R from 'ramda'
import debounce from 'debounce'
import AppClosingBanner from '@/components/AppClosingBanner.vue'

export default {
  name: 'App',
  components: {
    RgToolbar,
    RgSidenav,
    RgLoader,
    ButtonToTop,
    EmptyState,
    AppClosingBanner
  },
  data () {
    return {
      notyOffline: null,
      namesForCache: ['my-library', 'my-book']
    }
  },
  watch: {
    isOffline (val) {
      if (val && !this.notyOffline) {
        this.showOfflineNoty()
      }

      if (!val && this.notyOffline) {
        this.notyOffline.close()
        this.showOnlineNoty()
      }
    }
  },
  methods: {
    showOnlineNoty: debounce(function () {
      this.$noty(this.$ntype.ONLINE_RECOVERY)
    }, 750),
    showOfflineNoty () {
      this.notyOffline = this.$noty(this.$ntype.OFFLINE).on(
        'afterClose',
        () => {
          this.notyOffline = null
        }
      )
    }
  },
  computed: {
    ...mapGetters([
      'viewPending',
      'viewErrorCode',
      'isOffline',
      'access',
      'appBlocked',
      'isLoggedIn',
      'closingTimeLocale',
      'isAfterClosing',
      'isAfterNotice'
    ]),
    errorMessage () {
      if (this.viewErrorCode === 'app/timeout') {
        return this.$t('errors.timeout')
      }
      if (this.viewErrorCode === 'PERMISSION_DENIED') {
        return this.$t('errors.denied')
      }
      return this.$t('errors.unknown')
    },
    pending () {
      return R.isNil(this.appBlocked)
    },
    unknownError () {
      return this.viewErrorCode === 'unknown'
    },
    emailHref () {
      return `mailto:contact@readgraphyapp.com?subject=${this.$t(
        'emailSubject'
      )}`
    }
  },
  created () {
    if (this.isOffline) {
      this.showOfflineNoty()
    }
  },
  i18n: {
    messages: {
      ko: {
        links: {
          email: '관리자에게 메일 보내기'
        },
        emailSubject: '리드그라피%20에러',
        inprogress: '처리중...',
        errors: {
          timeout: '서버 응답시간이 초과되었습니다.',
          denied: '잘못된 데이터로 인해 에러가 발생했습니다.',
          unknown: '알 수 없는 에러가 발생했습니다.'
        },
        closingMessage: '{closingTime}부로 서비스가 종료되었습니다.'
      },
      en: {
        links: {
          email: 'send email to admin'
        },
        emailSubject: 'readgraphy%20error',
        inprogress: 'in progress...',
        errors: {
          timeout: 'Server response timed out.',
          denied: 'An error occurred because of invalid data.',
          unknown: 'An unknown error has occurred.'
        },
        closingMessage: 'The service ended at {closingTime} Seoul time.'
      }
    }
  }
}
</script>

<style>
@import "~@/stylesheets/noty.css";

html {
  box-sizing: border-box;
}

*,
*::before,
*::after {
  box-sizing: inherit;
}

body {
  background: #fafafa;
}

@media only screen and (max-width: 767px) {
  .ui.unstackable.items > .item > .image,
  .ui.unstackable.items > .item > .image > img {
    width: 80px !important;
  }

  .ui.tiny.image {
    width: initial;
  }
}

ul {
  padding: 0;
}

.ui.tiny.image {
  width: 100px;
}

.fullscreen-v-img {
  z-index: 110;
}

.prev-v-img,
.next-v-img {
  opacity: 0.5 !important;
}
</style>

<style module>
@value toolbarPadder, padder, contentWidth from '~@/stylesheets/app.css';

.notice {
  margin: 20px 0;
}

.mail {
  display: block;
  margin: 25px 0;
  font-size: 1rem;
}
</style>
